import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { endpoint } from '..';
import Button from '../../../components/Button/Button';

import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '../../../components/Dialog/Dialog';
import TextArea from '../../../components/TextArea/TextArea';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import Layout from '../../components/Layout/Layout';
import { EProjectType, unit } from '../../enum/project';
import { setProjectInfo as macro1SetProjectInfo } from '../../Macro1/store/project/projectslice';
import { setProjectInfo as macro2SetProjectInfo } from '../../Macro2/store/project/projectslice';
import { selectProjectInfo, setProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { ICountry, IProjectInfoForm, IProjectInfoProps } from './IProjectInfo';

import { Grid, MenuItem, Select } from '@mui/material';
import { useEffect, useState } from 'react';
import Map from '../../../components/Map/Map';
import { commitProject as commitProjectM1, dataToProject as dataToProjectM1, setProject as setProjectM1 } from '../../Macro1/store/project/projectslice';
import { commitProject as commitProjectM2, dataToProject as dataToProjectM2, setProject as setProjectM2 } from '../../Macro2/store/project/projectslice';
import './ProjectInfo.scss';

const defaultCoordinate = [44.5215, 11.2833] as [number, number];

const ProjectInfo = (props: IProjectInfoProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const projectInfo = useAppSelector(selectProjectInfo);

    const [searchParams] = useSearchParams();

    const [countries, setCountries] = useState<ICountry[]>([]);
    const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
    const [loadingProject, setLoadingProject] = useState<boolean>(true);
    const [ErrorDialog, setErrorDialog] = useState<string>('');
    const [disableNext, setDisableNext] = useState<boolean>(false);

    const requiredMSG = t('project_info_formik_required');

    const closeErrorDialog = () => {
        setErrorDialog('');
    };

    // Caricamento del progetto in base a id e macro
    useEffect(() => {
        const fetchProject = async (id: string, macro2: boolean) => {
            if (macro2) {
                const res = await BeMaccaferriStoreHelper.getMacro2ProjectAsync(id);
                if (res.data.projectInfos) {
                    dispatch(setProjectInfo({ projectId: id, ...res.data.projectInfos, hasBeenSent: false }));
                }
                dispatch(setProjectM2(dataToProjectM2(res.data)));
                dispatch(commitProjectM2(dataToProjectM2(res.data)));
            } else {
                const res = await BeMaccaferriStoreHelper.getMacro1ProjectAsync(id);
                if (res.data.projectInfos) {
                    dispatch(setProjectInfo({ projectId: id, ...res.data.projectInfos, hasBeenSent: false }));
                }
                dispatch(setProjectM1(dataToProjectM1(res.data)));
                dispatch(commitProjectM1(dataToProjectM1(res.data)));
            }
            setLoadingProject(false);
        };

        if (searchParams.get('id')) {
            fetchProject(searchParams.get('id') ?? '', searchParams.get('macro') === '2');
        } else {
            setLoadingProject(false);
        }
    }, [dispatch, searchParams]);

    // Caricamento della lista di paesi
    useEffect(() => {
        const fetchCountries = async (countryName?: string) => {
            let res = await BeMaccaferriStoreHelper.getProjectCountriesAsync(countryName);
            return res.data;
        };

        const fetchData = async () => {
            const response = await fetchCountries();
            if (response !== undefined) {
                setCountries(response);
            }
            setLoadingCountries(false);
        };

        fetchData();
    }, []);

    // Costruisce i valori iniziali (prelevati dallo store)
    const handleInitialValues = (): IProjectInfoForm => {
        return {
            projectId: projectInfo.data?.projectId ?? undefined,
            projectName: projectInfo.data?.projectName ?? '',
            owner: projectInfo.data?.owner ?? '',
            nrProject: projectInfo.data?.nrProject ?? '1',
            projectType: projectInfo.data?.projectType ?? ('' as EProjectType),
            notes: projectInfo.data?.notes ?? '',
            designerFirmName: projectInfo.data?.designerFirmName ?? '',
            designerFirmCountry: projectInfo.data?.designerFirmCountry ?? 'IT',
            designerFirmCity: projectInfo.data?.designerFirmCity ?? '',
            coordinates: projectInfo.data?.coordinates ?? defaultCoordinate,
            productCountry: projectInfo.data?.productCountry ?? null,
            mapImage: projectInfo.data?.mapImage ?? '',
            unit: projectInfo.data?.unit ?? unit.SI,
        };
    };

    const ProjectSchema = Yup.object().shape({
        projectName: Yup.string().required(requiredMSG),
        owner: Yup.string().required(requiredMSG),
        nrProject: Yup.string(), // se vuoi obbligatorio: .required(requiredMSG)
        designerFirmName: Yup.string().required(requiredMSG),
        designerFirmCountry: Yup.string().required(requiredMSG),
        designerFirmCity: Yup.string().required(requiredMSG),
        notes: Yup.string(),
        coordinates: Yup.array().test('Test_coordinates', requiredMSG, function (value) {
            return !!value && value[0] !== undefined && value[1] !== undefined;
        }),
    });

    const handleSubmit = async (values: IProjectInfoForm, { setSubmitting }: FormikHelpers<IProjectInfoForm>) => {
        setDisableNext(true);
        try {
            const productCountryRes = await BeMaccaferriStoreHelper.getCountryByCoordinatesAsync(values.coordinates[0]!, values.coordinates[1]!);
            // Salvo i dati in Redux + immagine mappa
            const projectInfoToDispatch: IProjectInfoForm = { ...values };
            const productCountry = productCountryRes.extratags['ISO3166-1:alpha2'];
            projectInfoToDispatch.productCountry = productCountry;
            dispatch(setProjectInfo(projectInfoToDispatch));

            // Se � la prima compilazione, vado allo step successivo
            if (projectInfo.initialState) {
                navigate(endpoint.projectType);
            } else {
                // Altrimenti aggiorno e vado subito in designCondition
                if (projectInfo.data?.projectType === EProjectType.Macro1) {
                    macro1SetProjectInfo(projectInfoToDispatch);
                } else {
                    macro2SetProjectInfo(projectInfoToDispatch);
                }
                try {
                    await BeMaccaferriStoreHelper.putProjectAsync({
                        ...values,
                        productCountry,
                        id: values.projectId,
                    });
                    window.location.href = `/designCondition/${values.projectType}/${values.projectId}`;
                } catch (ex) {
                    console.error(ex);
                    setErrorDialog('error');
                }
            }
        } catch (ex) {
            console.error(ex);
            setErrorDialog('coordinates');
        }
        setSubmitting(false);
        setDisableNext(false);
    };

    return (
        <Layout>
            <Dialog
                open={!!ErrorDialog && ErrorDialog.length > 0}
                title={`${t(`errors.${ErrorDialog === 'coordinates' ? 'projectCoordinatesCountry' : 'projectPut'}.title`)}`}
                onClose={closeErrorDialog}
                actions={[
                    {
                        text: 'ok',
                        callback: closeErrorDialog,
                        color: 'primary',
                    },
                ]}>
                <div>{`${t(`errors.${ErrorDialog === 'coordinates' ? 'projectCoordinatesCountry' : 'projectPut'}.message`)}`}</div>
            </Dialog>

            <Grid container style={{ paddingTop: '80px', paddingBottom: '80px', alignItems: 'center' }} spacing={2}>
                <Grid item xs={12}>
                    <label className='tableTitle'>{t('project_info_title')}</label>
                </Grid>

                <Grid item xs={12}>
                    {/* Mostra il form solo quando sono stati caricati Paesi e Progetto */}
                    {!loadingCountries && !loadingProject && (
                        <Formik initialValues={handleInitialValues()} validationSchema={ProjectSchema} enableReinitialize={true} onSubmit={handleSubmit}>
                            {({ values, setValues, handleChange, isSubmitting, submitForm }) => {
                                return (
                                    <Form className='d-flex align-items-center h-100 project-info-form overflow-auto'>
                                        <div className='d-flex justify-content-between w-100 full-height'>
                                            <Grid container item spacing={2} style={{ alignItems: 'stretch' }}>
                                                {/* SEZIONE PROGETTO */}
                                                <Grid item xs={12} md={6}>
                                                    <div className='d-flex flex-column gap-1'>
                                                        <h6 className='formSectionTitle'>{t('project_info_project_detail_title')}</h6>

                                                        <div className='d-flex flex-column'>
                                                            <label className='formLabel'>{t('project_info_project_detail_project_name')}</label>
                                                            <Field type='text' title={t('project_info_project_detail_project_name')} name='projectName' placeholder={t('placeholder') as string} />
                                                            <ErrorMessage name='projectName' component='span' className='error-color' />
                                                        </div>

                                                        <div className='d-flex flex-column'>
                                                            <label className='formLabel'>{t('project_info_project_detail_project_number')}</label>
                                                            <Field type='text' title={t('project_info_project_detail_project_number')} name='nrProject' placeholder={t('placeholder') as string} />
                                                            <ErrorMessage name='nrProject' component='span' className='error-color' />
                                                        </div>

                                                        <div className='d-flex flex-column'>
                                                            <label className='formLabel'>{t('project_info_project_detail_owner')}</label>
                                                            <Field type='text' name='owner' placeholder={t('placeholder') as string} />
                                                            <ErrorMessage name='owner' component='span' className='error-color' />
                                                        </div>
                                                    </div>

                                                    {/* SEZIONE PROGETTISTA */}
                                                    <h6 className='formSectionTitle' style={{ marginTop: 50 }}>
                                                        {t('project_info_designer_detail_title')}
                                                    </h6>
                                                    <div className='d-flex flex-column'>
                                                        <label className='formLabel'>{t('project_info_designer_detail_firm')}</label>
                                                        <Field type='text' title={t('project_info_designer_detail_firm')} name='designerFirmName' placeholder={t('placeholder') as string} />
                                                        <ErrorMessage name='designerFirmName' component='span' className='error-color' />
                                                    </div>

                                                    <div className='d-flex flex-column'>
                                                        <label className='formLabel'>{t('project_info_designer_detail_country')}</label>
                                                        <Select id='country-select' name='designerFirmCountry' size='small' value={values.designerFirmCountry} onChange={handleChange}>
                                                            {countries.map((country: ICountry) => (
                                                                <MenuItem selected={country.alpha2Code === values.designerFirmCountry} key={country.alpha2Code} value={country.alpha2Code}>
                                                                    {country.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        <ErrorMessage name='designerFirmCountry' component='span' className='error-color' />
                                                    </div>

                                                    <div className='d-flex flex-column'>
                                                        <label className='formLabel'>{t('project_info_designer_detail_city')}</label>
                                                        <Field type='text' title={t('project_info_designer_detail_city')} name='designerFirmCity' placeholder={t('placeholder') as string} />
                                                        <ErrorMessage name='designerFirmCity' component='span' className='error-color' />
                                                    </div>
                                                </Grid>

                                                {/* SEZIONE MAPPA, LAT/LNG */}
                                                <Grid item xs={12} md={6} style={{ alignItems: 'stretch' }}>
                                                    <div className='d-flex flex-column gap-1 full-height'>
                                                        <div className='d-flex flex-column'>
                                                            <h6 className='formSectionTitle'>{t('project_info_location')}</h6>

                                                            <Grid container item spacing={2}>
                                                                <Grid item xs={6}>
                                                                    <div className='d-flex flex-column'>
                                                                        <label className='formLabel'>{t('project_info_project_detail_location_latitude')}</label>
                                                                        <Field
                                                                            type='number'
                                                                            name='coordinates[0]'
                                                                            placeholder={t('placeholder') as string}
                                                                            value={values.coordinates[0] ?? defaultCoordinate[0]}
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <div className='d-flex flex-column'>
                                                                        <label className='formLabel'>{t('project_info_project_detail_location_longitude')}</label>
                                                                        <Field
                                                                            type='number'
                                                                            name='coordinates[1]'
                                                                            placeholder={t('placeholder') as string}
                                                                            value={values.coordinates[1] ?? defaultCoordinate[1]}
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>
                                                                </Grid>
                                                            </Grid>

                                                            <ErrorMessage name='coordinates' component='span' className='error-color' />

                                                            <div className='d-flex flex-column' style={{ marginTop: 10 }}>
                                                                <Map
                                                                    coordinates={{
                                                                        latitude: values.coordinates[0] ?? defaultCoordinate[0],
                                                                        longitude: values.coordinates[1] ?? defaultCoordinate[1],
                                                                    }}
                                                                    onClick={(coordinate, mapImage) => {
                                                                        setValues(stateValues => ({
                                                                            ...stateValues,
                                                                            mapImage,
                                                                            coordinates: [coordinate.latitude as number, coordinate.longitude as number],
                                                                        }));
                                                                    }}
                                                                />
                                                            </div>

                                                            <ErrorMessage name='productCountry' component='span' className='error-color' />
                                                        </div>

                                                        <div className='d-flex flex-column next-container' />
                                                    </div>
                                                </Grid>

                                                {/* SEZIONE NOTE */}
                                                <Grid item xs={12} style={{ alignItems: 'stretch' }}>
                                                    <div className='d-flex flex-column' style={{ marginTop: 10 }}>
                                                        <TextArea
                                                            title={t('project_info_project_detail_notes')}
                                                            name='notes'
                                                            rows={5}
                                                            value={values.notes}
                                                            placeholder={t('placeholder') as string}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </Grid>

                                                {/* PULSANTE SUBMIT */}
                                                <Grid
                                                    item
                                                    xs={12}
                                                    style={{
                                                        alignItems: 'stretch',
                                                        paddingBottom: 16,
                                                        display: 'flex',
                                                        flexDirection: 'row-reverse',
                                                    }}>
                                                    <div style={{ marginLeft: 8 }}>
                                                        <Button onClick={submitForm} disabled={isSubmitting || disableNext} className='d-flex justify-content-end'>
                                                            {!projectInfo.initialState ? t('project_info_go_to_design_condition') : t('next_button')}
                                                        </Button>
                                                    </div>
                                                    {disableNext && <CircularProgress sx={{ marginLeft: 8 }} />}
                                                </Grid>
                                            </Grid>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Grid>
            </Grid>
        </Layout>
    );
};

export default ProjectInfo;
